import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SaveIcon from '@mui/icons-material/Save';
import {
  conectarEmailGmail,
  desconectarEmailGmail,
  getEmailStatus,
  removerEmailSmtp,
  salvarEmailSmtp,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const emptySmtp = () => ({
  host: '',
  porta: 587,
  usuario: '',
  senha: '',
  from: '',
  fromName: '',
  enableSsl: true,
});

export default function EmailSettingsPage() {
  const [status, setStatus] = useState(null);
  const [smtpForm, setSmtpForm] = useState(emptySmtp());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');

  const carregar = useCallback(async () => {
    try {
      const { data } = await getEmailStatus();
      setStatus(data);
      if (data?.smtp?.configurado) {
        setSmtpForm({
          host: data.smtp.host || '',
          porta: data.smtp.porta || 587,
          usuario: data.smtp.usuario || '',
          senha: '',
          from: data.smtp.from || '',
          fromName: data.smtp.fromName || '',
          enableSsl: data.smtp.enableSsl !== false,
        });
      }
      if (data?.erro && !data?.conectado) setErro(data.erro);
      else setErro('');
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível consultar o e-mail.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('email');
    if (flag === 'conectado') {
      setOk('Gmail conectado com sucesso.');
      params.delete('email');
      params.delete('msg');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    } else if (flag === 'erro') {
      setErro(params.get('msg') || 'Falha ao conectar o Gmail.');
      params.delete('email');
      params.delete('msg');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
    carregar();
  }, [carregar]);

  const conectar = async () => {
    setBusy(true);
    setErro('');
    setOk('');
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      const { data } = await conectarEmailGmail({ returnUrl });
      if (!data?.url) throw new Error('URL de autorização não retornada.');
      window.location.href = data.url;
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível iniciar a conexão com o Google.'));
      setBusy(false);
    }
  };

  const desconectarGmail = async () => {
    setBusy(true);
    setErro('');
    setOk('');
    try {
      const { data } = await desconectarEmailGmail();
      setStatus(data);
      setOk('Gmail desconectado.');
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível desconectar o Gmail.'));
    } finally {
      setBusy(false);
    }
  };

  const salvarSmtp = async () => {
    setBusy(true);
    setErro('');
    setOk('');
    try {
      const { data } = await salvarEmailSmtp({
        host: smtpForm.host,
        porta: Number(smtpForm.porta) || 587,
        usuario: smtpForm.usuario || null,
        senha: smtpForm.senha || null,
        from: smtpForm.from,
        fromName: smtpForm.fromName || null,
        enableSsl: !!smtpForm.enableSsl,
      });
      setStatus(data);
      setSmtpForm((f) => ({ ...f, senha: '' }));
      setOk('SMTP salvo.');
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível salvar o SMTP.'));
    } finally {
      setBusy(false);
    }
  };

  const limparSmtp = async () => {
    setBusy(true);
    setErro('');
    setOk('');
    try {
      const { data } = await removerEmailSmtp();
      setStatus(data);
      setSmtpForm(emptySmtp());
      setOk('SMTP removido.');
    } catch (e) {
      setErro(getApiErrorMessage(e, 'Não foi possível remover o SMTP.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const gmailOk = Boolean(status?.gmail?.conectado);
  const smtpOk = Boolean(status?.smtp?.configurado);
  const googleApp = Boolean(status?.appConfigurado ?? status?.gmail?.appConfigurado);

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h6">E-mail da loja</Typography>
        <Typography variant="body2" color="text.secondary">
          Configure SMTP e/ou conecte o Gmail. Se os dois estiverem ativos, o envio usa o Gmail;
          caso contrário, usa o SMTP. Cada instância tem a própria configuração.
        </Typography>
      </Box>

      {erro && <Alert severity="error" onClose={() => setErro('')}>{erro}</Alert>}
      {ok && <Alert severity="success" onClose={() => setOk('')}>{ok}</Alert>}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Chip
          color={status?.conectado ? 'success' : 'default'}
          label={status?.conectado
            ? `Pronto · ${status.provedor === 'gmail' ? 'Gmail' : 'SMTP'}`
            : 'Nenhum canal'}
        />
        {status?.email && (
          <Typography variant="body2">{status.email}</Typography>
        )}
      </Stack>

      <Divider />

      <Stack spacing={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>SMTP</Typography>
        <Typography variant="body2" color="text.secondary">
          Servidor de e-mail da loja (host, autenticação e remetente).
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Servidor (host)"
            value={smtpForm.host}
            onChange={(e) => setSmtpForm((f) => ({ ...f, host: e.target.value }))}
            fullWidth
            required
            placeholder="smtp.seudominio.com"
          />
          <TextField
            label="Porta"
            type="number"
            value={smtpForm.porta}
            onChange={(e) => setSmtpForm((f) => ({ ...f, porta: e.target.value }))}
            sx={{ width: { md: 120 } }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Usuário"
            value={smtpForm.usuario}
            onChange={(e) => setSmtpForm((f) => ({ ...f, usuario: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Senha"
            type="password"
            value={smtpForm.senha}
            onChange={(e) => setSmtpForm((f) => ({ ...f, senha: e.target.value }))}
            fullWidth
            helperText={smtpOk ? 'Deixe em branco para manter a senha atual.' : ' '}
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Remetente (From)"
            value={smtpForm.from}
            onChange={(e) => setSmtpForm((f) => ({ ...f, from: e.target.value }))}
            fullWidth
            required
            placeholder="nfe@seudominio.com"
          />
          <TextField
            label="Nome do remetente"
            value={smtpForm.fromName}
            onChange={(e) => setSmtpForm((f) => ({ ...f, fromName: e.target.value }))}
            fullWidth
            placeholder="Zen Gestão"
          />
        </Stack>
        <FormControlLabel
          control={(
            <Checkbox
              checked={!!smtpForm.enableSsl}
              onChange={(e) => setSmtpForm((f) => ({ ...f, enableSsl: e.target.checked }))}
            />
          )}
          label="SSL / TLS"
        />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={busy}
            onClick={salvarSmtp}
          >
            Salvar SMTP
          </Button>
          {smtpOk && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<LinkOffIcon />}
              disabled={busy}
              onClick={limparSmtp}
            >
              Remover SMTP
            </Button>
          )}
        </Stack>
      </Stack>

      <Divider />

      <Stack spacing={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>Gmail (OAuth)</Typography>
        {!googleApp ? (
          <Alert severity="info">
            Gmail OAuth não está habilitado neste servidor
            {' '}
            (<code>GoogleEmail:ClientId/Secret/RedirectUri</code>
            ). O SMTP acima funciona normalmente.
          </Alert>
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                size="small"
                color={gmailOk ? 'success' : 'default'}
                label={gmailOk ? 'Gmail conectado' : 'Gmail desconectado'}
              />
              {gmailOk && status?.gmail?.email && (
                <Typography variant="body2">{status.gmail.email}</Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {!gmailOk ? (
                <Button
                  variant="outlined"
                  startIcon={<EmailOutlinedIcon />}
                  disabled={busy}
                  onClick={conectar}
                >
                  {busy ? 'Abrindo Google…' : 'Conectar Gmail'}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<EmailOutlinedIcon />}
                    disabled={busy}
                    onClick={conectar}
                  >
                    Trocar conta
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<LinkOffIcon />}
                    disabled={busy}
                    onClick={desconectarGmail}
                  >
                    Desconectar Gmail
                  </Button>
                </>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Stack>
  );
}
